if (!customElements.get('cms-email-popup')) {
  const TRIGGER_VISIBLE_TIME_MS = 30_000;
  const TRIGGER_UNIQUE_PAGE_COUNT = 2;
  const TRIGGER_STATE_VERSION = 2;
  const DEFAULT_DISMISSAL_DAYS = 14;
  const DEFAULT_ANONYMOUS_SIGNUP_SUCCESS_DAYS = 14;
  const MIN_SUPPRESSION_DAYS = 1;
  const MAX_SUPPRESSION_DAYS = 90;

  customElements.define(
    'cms-email-popup',
    class CMSEmailPopup extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === 'true') return;

        this.modal = this.querySelector('.cms-email-popup__modal');
        this.form = this.querySelector('.cms-email-popup__form');
        this.input = this.querySelector('.cms-email-popup__input');
        this.submitButton = this.querySelector('.cms-email-popup__button');
        this.errorMessage = this.querySelector('.cms-email-popup__error');
        this.defaultSubmitButtonLabel =
          this.submitButton
            ?.querySelector('.cms-email-popup__button-label')
            ?.textContent?.trim() || 'Sign Up';
        this.formState = Array.from(
          this.querySelectorAll('[data-cms-email-popup-form-state]')
        );
        this.successState = Array.from(
          this.querySelectorAll('[data-cms-email-popup-success-state]')
        );
        this.serverSuccess = Boolean(this.querySelector('[data-cms-email-popup-server-success]'));
        this.serverError = Boolean(this.querySelector('[data-cms-email-popup-server-error]'));
        this.seenStorageKey = this.dataset.seenStorageKey || 'cms-email-popup-seen';
        this.submissionStorageKey = `${this.seenStorageKey}:submission-pending`;
        this.submissionScrollStorageKey = `${this.seenStorageKey}:submission-scroll-y`;
        this.courseStorageKey =
          this.dataset.courseStorageKey || 'cms-email-popup-course-materials-entry';
        this.entryEvaluatedKey = `${this.courseStorageKey}:evaluated`;
        this.configElement = this.closest('[data-cms-email-popup-config]');
        this.subscriberState =
          this.configElement?.dataset.cmsEmailPopupSubscriberState || 'anonymous';
        this.dismissalDays = this.parseSuppressionDays(
          this.configElement?.dataset.cmsEmailPopupDismissalDays,
          DEFAULT_DISMISSAL_DAYS
        );
        this.anonymousSignupSuccessDays = this.parseSuppressionDays(
          this.configElement?.dataset.cmsEmailPopupAnonymousSignupSuccessDays,
          DEFAULT_ANONYMOUS_SIGNUP_SUCCESS_DAYS
        );
        this.storageKeyPrefix =
          this.configElement?.dataset.cmsEmailPopupStorageKeyPrefix ||
          this.seenStorageKey.replace(/-seen$/, '') ||
          'cms-email-popup';
        this.triggerStorageKey = `${this.storageKeyPrefix}:trigger`;
        this.dismissalStorageKey = `${this.storageKeyPrefix}:dismissed-at`;
        this.anonymousSignupSuccessStorageKey = `${this.storageKeyPrefix}:anonymous-signup-success`;
        this.isDesignMode = this.dataset.designMode === 'true';
        this.isOpen = false;
        this.isSubmitting = false;
        this.autoDisplayActive = false;
        this.autoOpenPending = false;
        this.triggerState = null;
        this.visibleStartedAt = null;
        this.currentPageCounted = false;
        this.touchStartY = null;
        this.touchStartX = null;
        this.touchStartedAt = null;
        this.dragOffset = 0;

        if (!this.modal) return;

        this.dataset.initialized = 'true';
        this.form?.setAttribute('novalidate', 'novalidate');
        this.updateReturnTo();
        this.sessionStorage = this.getAvailableStorage('sessionStorage');
        this.localStorage = this.getAvailableStorage('localStorage');

        if (this.serverSuccess || (!this.serverError && this.isUrlSubmissionSuccess())) {
          this.showSuccessState();
          this.restoreSubmissionScrollPosition();
        } else if (!this.isChallengePage() && this.getSessionValue(this.submissionStorageKey)) {
          this.removeSessionValue(this.submissionStorageKey);
          if (this.serverError) {
            this.restoreSubmissionScrollPosition();
          } else {
            this.removeSessionValue(this.submissionScrollStorageKey);
          }
        }

        this.popupFontsReady = this.preparePopupFonts();

        this.onBackdropClick = this.handleBackdropClick.bind(this);
        this.onKeydown = this.handleKeydown.bind(this);
        this.onCookieClick = this.handleCookieClick.bind(this);
        this.onTouchStart = this.handleTouchStart.bind(this);
        this.onTouchMove = this.handleTouchMove.bind(this);
        this.onTouchEnd = this.handleTouchEnd.bind(this);
        this.onVisibilityChange = this.handleVisibilityChange.bind(this);
        this.onPageHide = this.handlePageHide.bind(this);
        this.onPageShow = this.handlePageShow.bind(this);

        this.addEventListener('click', this.onBackdropClick);
        this.querySelectorAll('[data-cms-email-popup-cookie-preferences]').forEach((link) => {
          link.addEventListener('click', this.onCookieClick);
        });
        this.modal.addEventListener('touchstart', this.onTouchStart, { passive: true });
        this.modal.addEventListener('touchmove', this.onTouchMove, { passive: false });
        this.modal.addEventListener('touchend', this.onTouchEnd, { passive: true });

        this.querySelectorAll('[data-cms-email-popup-close]').forEach((button) => {
          button.addEventListener('click', () => this.close());
        });

        this.bindForm();
        this.initializeDisplay();
      }

      disconnectedCallback() {
        window.clearTimeout(this.openTimer);
        this.stopTriggerTracking({ persist: true });
        document.removeEventListener('keydown', this.onKeydown);
        document.body.classList.remove('cms-email-popup-open');
      }

      initializeDisplay() {
        if (this.isChallengePage()) return;

        if (this.serverSuccess || this.serverError) {
          this.queueOpen(0, false);
          return;
        }

        if (this.isDesignMode) {
          this.queueOpen(0, false);
          return;
        }

        if (this.dataset.enabled !== 'true') {
          this.clearTriggerState();
          return;
        }
        if (this.shouldSuppressForCourseMaterials()) {
          this.clearTriggerState();
          return;
        }
        if (this.getSessionValue(this.seenStorageKey) === '1') return;

        if (this.subscriberState === 'subscribed') {
          this.clearTriggerState();
          return;
        }

        if (!this.sessionStorage || !this.localStorage) {
          this.clearTriggerState();
          return;
        }
        if (this.shouldSuppressForSubscriber() || this.shouldSuppressForDismissal()) {
          this.clearTriggerState();
          return;
        }

        this.startTriggerTracking();
      }

      queueOpen(delay, markSeen) {
        window.clearTimeout(this.openTimer);
        this.openTimer = window.setTimeout(async () => {
          if (markSeen && this.getSessionValue(this.seenStorageKey) === '1') return;

          await this.popupFontsReady;
          if (!this.isConnected) return;
          if (markSeen && this.getSessionValue(this.seenStorageKey) === '1') return;

          this.open(markSeen);
        }, delay);
      }

      async preparePopupFonts() {
        if (!document.fonts?.load) return;

        const headingSelector = this.serverSuccess
          ? '.cms-email-popup__heading--success'
          : '[data-cms-email-popup-form-state] .cms-email-popup__heading';
        const bodySelector = this.serverSuccess
          ? '.cms-email-popup__success-message'
          : '.cms-email-popup__description';
        const headingText =
          this.querySelector(headingSelector)?.textContent?.trim() || 'Email signup';
        const bodyText = this.querySelector(bodySelector)?.textContent?.trim() || 'Email signup';
        const buttonText =
          this.querySelector('.cms-email-popup__button-label')?.textContent?.trim() || 'Sign up';

        await Promise.allSettled([
          document.fonts.load('800 32px "Plus Jakarta Sans"', headingText),
          document.fonts.load('400 14px "Inter"', bodyText),
          document.fonts.load('700 14px "Plus Jakarta Sans"', buttonText),
          document.fonts.ready,
        ]);
      }

      open(markSeen = true) {
        if (this.isOpen) return;

        this.previousFocus = document.activeElement;
        this.hidden = false;
        this.isOpen = true;
        document.body.classList.add('cms-email-popup-open');
        document.addEventListener('keydown', this.onKeydown);

        if (markSeen) this.setSessionValue(this.seenStorageKey, '1');

        window.requestAnimationFrame(() => {
          const focusTarget = this.serverSuccess ? this.modal : this.input || this.modal;
          focusTarget?.focus({ preventScroll: true });
        });
      }

      close({ persistDismissal = true } = {}) {
        if (!this.isOpen) return;

        if (persistDismissal && !this.isDesignMode) this.recordDismissal();

        this.resetDrag();
        this.hidden = true;
        this.isOpen = false;
        document.body.classList.remove('cms-email-popup-open');
        document.removeEventListener('keydown', this.onKeydown);

        if (this.previousFocus instanceof HTMLElement) {
          this.previousFocus.focus({ preventScroll: true });
        }
      }

      bindForm() {
        if (!this.form || !this.input || !this.submitButton) return;

        this.form.addEventListener('submit', (event) => {
          if (this.isSubmitting) {
            event.preventDefault();
            return;
          }

          const validationMessage = this.getValidationMessage(this.input.value);
          if (validationMessage) {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.showError(validationMessage);
            this.input.focus();
            return;
          }

          this.hideError();
          this.updateReturnTo();
          this.setSessionValue(this.submissionStorageKey, this.form.id || 'pending');
          this.setSessionValue(this.submissionScrollStorageKey, String(window.scrollY));
          this.setSubmittingState(true);
        });

        this.input.addEventListener('input', () => this.hideError());
      }

      updateReturnTo() {
        const returnToInput = this.form?.querySelector('input[name="return_to"]');
        if (!returnToInput) return;

        const returnToUrl = new URL(window.location.href);
        returnToUrl.searchParams.delete('customer_posted');
        returnToInput.value = `${returnToUrl.pathname}${returnToUrl.search}`;
      }

      restoreSubmissionScrollPosition() {
        const storedScrollY = this.getSessionValue(this.submissionScrollStorageKey);
        this.removeSessionValue(this.submissionScrollStorageKey);

        if (storedScrollY === null) return;

        const scrollY = Number.parseFloat(storedScrollY);
        if (!Number.isFinite(scrollY) || scrollY < 0) return;

        window.requestAnimationFrame(() => {
          window.scrollTo(window.scrollX, scrollY);
        });
      }

      setSubmittingState(isSubmitting) {
        this.isSubmitting = isSubmitting;
        if (!this.submitButton) return;

        this.submitButton.disabled = isSubmitting;

        const label = this.submitButton.querySelector('.cms-email-popup__button-label');
        if (label) {
          label.textContent = isSubmitting
            ? this.submitButton.dataset.submittingLabel || 'Submitting...'
            : this.defaultSubmitButtonLabel;
        }
      }

      isUrlSubmissionSuccess() {
        if (!this.form?.id || this.isChallengePage()) return false;

        const params = new URLSearchParams(window.location.search);
        const submittedFormId = this.getSessionValue(this.submissionStorageKey);
        const hashFormId = window.location.hash.slice(1);
        const hasShopifySuccessParam = params.get('customer_posted') === 'true';

        if (hasShopifySuccessParam) {
          return hashFormId === this.form.id || submittedFormId === this.form.id;
        }

        return hashFormId === this.form.id && submittedFormId === this.form.id;
      }

      isChallengePage() {
        return /\/challenge\/?$/.test(window.location.pathname);
      }

      showSuccessState() {
        this.formState.forEach((element) => {
          element.hidden = true;
        });
        this.successState.forEach((element) => {
          element.hidden = false;
        });

        this.serverSuccess = true;
        this.classList.add('cms-email-popup--success');
        this.setSubmittingState(false);
        this.setLocalValue(this.anonymousSignupSuccessStorageKey, String(Date.now()));
        this.setSessionValue(this.seenStorageKey, '1');
        this.clearTriggerState();
        this.removeSessionValue(this.submissionStorageKey);
      }

      getValidationMessage(rawEmail) {
        const email = String(rawEmail || '').trim();
        if (!email) return this.dataset.emptyError || 'Enter your email address.';
        if (!email.includes('@')) {
          return this.dataset.missingAtError || 'Email address must include @.';
        }

        const atIndex = email.lastIndexOf('@');
        const domain = email.slice(atIndex + 1);
        if (!domain.includes('.')) {
          return this.dataset.missingDotError || 'Email address must include a period after @.';
        }

        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
        if (!this.input.validity.valid || !emailPattern.test(email)) {
          return this.dataset.invalidError || 'Enter a valid email address.';
        }

        return '';
      }

      showError(message) {
        if (!this.errorMessage) return;
        this.errorMessage.textContent = message;
        this.errorMessage.hidden = false;
        this.input?.setAttribute('aria-invalid', 'true');
      }

      hideError() {
        if (!this.errorMessage || this.errorMessage.hidden) return;
        this.errorMessage.hidden = true;
        this.errorMessage.textContent = '';
        this.input?.removeAttribute('aria-invalid');
      }

      parseSuppressionDays(rawValue, defaultDays) {
        const parsedValue = Number.parseInt(String(rawValue || ''), 10);
        const suppressionDays = Number.isFinite(parsedValue) ? parsedValue : defaultDays;

        return Math.min(MAX_SUPPRESSION_DAYS, Math.max(MIN_SUPPRESSION_DAYS, suppressionDays));
      }

      shouldSuppressForSubscriber() {
        if (this.subscriberState === 'subscribed') return true;

        if (this.subscriberState === 'unsubscribed') {
          return !this.removeLocalValue(this.anonymousSignupSuccessStorageKey);
        }

        const anonymousSignupSuccess = this.readLocalTimestamp(
          this.anonymousSignupSuccessStorageKey
        );
        if (anonymousSignupSuccess.status === 'unavailable') return true;
        if (anonymousSignupSuccess.status === 'corrupt') {
          this.markCurrentSessionSuppressed();
          return true;
        }
        if (anonymousSignupSuccess.status === 'missing') return false;

        const suppressionPeriodMs =
          this.anonymousSignupSuccessDays * 24 * 60 * 60 * 1000;
        if (Date.now() - anonymousSignupSuccess.value < suppressionPeriodMs) return true;

        if (!this.removeLocalValue(this.anonymousSignupSuccessStorageKey)) return true;
        this.clearTriggerState();
        return false;
      }

      shouldSuppressForDismissal() {
        const dismissal = this.readLocalTimestamp(this.dismissalStorageKey);
        if (dismissal.status === 'unavailable') return true;
        if (dismissal.status === 'corrupt') {
          this.markCurrentSessionSuppressed();
          return true;
        }
        if (dismissal.status === 'missing') return false;

        const suppressionPeriodMs = this.dismissalDays * 24 * 60 * 60 * 1000;
        if (Date.now() - dismissal.value < suppressionPeriodMs) return true;

        if (!this.removeLocalValue(this.dismissalStorageKey)) return true;
        this.clearTriggerState();
        return false;
      }

      shouldSuppressAutomaticDisplay() {
        if (!this.sessionStorage || !this.localStorage) return true;

        const shouldSuppress =
          this.getSessionValue(this.seenStorageKey) === '1' ||
          this.shouldSuppressForCourseMaterials() ||
          this.shouldSuppressForSubscriber() ||
          this.shouldSuppressForDismissal();

        return shouldSuppress || !this.sessionStorage || !this.localStorage;
      }

      recordDismissal() {
        this.setLocalValue(this.dismissalStorageKey, String(Date.now()));
        this.setSessionValue(this.seenStorageKey, '1');
        this.stopTriggerTracking({ clearState: true });
      }

      markCurrentSessionSuppressed() {
        this.setSessionValue(this.seenStorageKey, '1');
        this.stopTriggerTracking({ clearState: true });
      }

      startTriggerTracking() {
        const storedState = this.readTriggerState();
        if (storedState.status === 'unavailable') return;

        if (storedState.status === 'valid') {
          this.triggerState = storedState.value;
        } else {
          if (storedState.status === 'corrupt') this.clearTriggerState();
          this.triggerState = {
            version: TRIGGER_STATE_VERSION,
            startedAt: null,
            visibleMs: 0,
            firstPageKey: null,
            pageCount: 0,
          };
        }

        this.autoDisplayActive = true;
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        window.addEventListener('pagehide', this.onPageHide);
        window.addEventListener('pageshow', this.onPageShow);

        if (document.visibilityState === 'visible') {
          this.resumeTriggerTracking();
        } else if (!this.persistTriggerState()) {
          this.stopTriggerTracking();
        }
      }

      stopTriggerTracking({ persist = false, clearState = false } = {}) {
        if (persist && this.autoDisplayActive) {
          this.commitVisibleTime();
          this.persistTriggerState();
        }

        this.autoDisplayActive = false;
        this.visibleStartedAt = null;
        window.clearTimeout(this.triggerTimer);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        window.removeEventListener('pagehide', this.onPageHide);
        window.removeEventListener('pageshow', this.onPageShow);

        if (clearState) this.clearTriggerState();
      }

      resumeTriggerTracking() {
        if (
          !this.autoDisplayActive ||
          !this.triggerState ||
          document.visibilityState !== 'visible' ||
          this.visibleStartedAt !== null
        ) {
          return;
        }

        this.countCurrentPage();
        const now = Date.now();
        if (this.triggerState.startedAt === null) this.triggerState.startedAt = now;
        this.visibleStartedAt = now;

        if (!this.persistTriggerState()) {
          this.stopTriggerTracking();
          return;
        }

        void this.evaluateAndOpen();
        this.scheduleTriggerTimer();
      }

      pauseTriggerTracking() {
        if (!this.autoDisplayActive) return;

        window.clearTimeout(this.triggerTimer);
        this.commitVisibleTime();
        if (!this.persistTriggerState()) this.stopTriggerTracking();
      }

      handleVisibilityChange() {
        if (document.visibilityState === 'visible') {
          this.resumeTriggerTracking();
        } else {
          this.pauseTriggerTracking();
        }
      }

      handlePageHide() {
        this.pauseTriggerTracking();
      }

      handlePageShow() {
        this.resumeTriggerTracking();
      }

      countCurrentPage() {
        if (this.currentPageCounted || !this.triggerState) return;

        const pageKey = this.getCurrentPageKey();
        if (!this.triggerState.firstPageKey) {
          this.triggerState.firstPageKey = pageKey;
          this.triggerState.pageCount = 1;
        } else if (
          this.triggerState.pageCount < TRIGGER_UNIQUE_PAGE_COUNT &&
          this.triggerState.firstPageKey !== pageKey
        ) {
          this.triggerState.pageCount = TRIGGER_UNIQUE_PAGE_COUNT;
        }

        this.currentPageCounted = true;
      }

      getCurrentPageKey() {
        const pathname = window.location.pathname || '/';
        return pathname === '/' ? pathname : pathname.replace(/\/+$/, '') || '/';
      }

      getCurrentVisibleTime() {
        if (!this.triggerState) return 0;

        const activeVisibleMs =
          this.visibleStartedAt === null ? 0 : Math.max(0, Date.now() - this.visibleStartedAt);

        return Math.min(
          TRIGGER_VISIBLE_TIME_MS,
          this.triggerState.visibleMs + activeVisibleMs
        );
      }

      commitVisibleTime() {
        if (!this.triggerState || this.visibleStartedAt === null) return;

        this.triggerState.visibleMs = this.getCurrentVisibleTime();
        this.visibleStartedAt = null;
      }

      scheduleTriggerTimer() {
        window.clearTimeout(this.triggerTimer);
        if (
          !this.autoDisplayActive ||
          !this.triggerState ||
          document.visibilityState !== 'visible'
        ) {
          return;
        }

        const remainingMs = TRIGGER_VISIBLE_TIME_MS - this.getCurrentVisibleTime();
        if (remainingMs <= 0) {
          void this.evaluateAndOpen();
          return;
        }

        this.triggerTimer = window.setTimeout(() => {
          this.commitVisibleTime();
          if (!this.persistTriggerState()) {
            this.stopTriggerTracking();
            return;
          }
          void this.evaluateAndOpen();
        }, remainingMs);
      }

      async evaluateAndOpen() {
        if (
          !this.autoDisplayActive ||
          this.autoOpenPending ||
          this.isOpen ||
          !this.triggerState ||
          this.triggerState.pageCount < TRIGGER_UNIQUE_PAGE_COUNT ||
          this.getCurrentVisibleTime() < TRIGGER_VISIBLE_TIME_MS
        ) {
          return;
        }

        this.autoOpenPending = true;

        try {
          this.commitVisibleTime();
          if (!this.persistTriggerState()) {
            this.stopTriggerTracking();
            return;
          }

          if (this.shouldSuppressAutomaticDisplay()) {
            this.stopTriggerTracking({ clearState: true });
            return;
          }

          await this.popupFontsReady;

          if (!this.isConnected || document.visibilityState !== 'visible') return;

          if (this.shouldSuppressAutomaticDisplay()) {
            this.stopTriggerTracking({ clearState: true });
            return;
          }

          this.stopTriggerTracking({ clearState: true });
          this.open(true);
        } finally {
          this.autoOpenPending = false;
        }
      }

      readTriggerState() {
        const rawState = this.getSessionValue(this.triggerStorageKey);
        if (rawState === null) {
          return { status: this.sessionStorage ? 'missing' : 'unavailable' };
        }

        try {
          const state = JSON.parse(rawState);
          const startedAtIsValid =
            state.startedAt === null ||
            (Number.isFinite(state.startedAt) && state.startedAt > 0);
          const emptyStateIsValid =
            state.pageCount === 0 &&
            state.startedAt === null &&
            state.visibleMs === 0 &&
            state.firstPageKey === null;
          const startedStateIsValid =
            state.pageCount >= 1 &&
            Number.isFinite(state.startedAt) &&
            state.startedAt > 0 &&
            typeof state.firstPageKey === 'string' &&
            state.firstPageKey.length > 0;

          if (
            state.version !== TRIGGER_STATE_VERSION ||
            !startedAtIsValid ||
            !Number.isFinite(state.visibleMs) ||
            state.visibleMs < 0 ||
            !Number.isInteger(state.pageCount) ||
            state.pageCount < 0 ||
            state.pageCount > TRIGGER_UNIQUE_PAGE_COUNT ||
            (!emptyStateIsValid && !startedStateIsValid)
          ) {
            return { status: 'corrupt' };
          }

          state.visibleMs = Math.min(TRIGGER_VISIBLE_TIME_MS, state.visibleMs);
          return { status: 'valid', value: state };
        } catch (error) {
          return { status: 'corrupt' };
        }
      }

      persistTriggerState() {
        if (!this.triggerState) return false;
        return this.setSessionValue(this.triggerStorageKey, JSON.stringify(this.triggerState));
      }

      clearTriggerState() {
        this.removeSessionValue(this.triggerStorageKey);
        this.triggerState = null;
      }

      handleBackdropClick(event) {
        if (event.target === this || event.target.classList?.contains('cms-email-popup__backdrop')) {
          this.close();
        }
      }

      handleCookieClick(event) {
        event.preventDefault();
        if (window.OneTrust && typeof window.OneTrust.ToggleInfoDisplay === 'function') {
          window.OneTrust.ToggleInfoDisplay();
        }
      }

      handleKeydown(event) {
        if (!this.isOpen) return;

        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
          return;
        }

        if (event.key !== 'Tab') return;

        const focusable = Array.from(
          this.modal.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
          )
        ).filter((element) => !element.hidden && element.offsetParent !== null);

        if (focusable.length === 0) {
          event.preventDefault();
          this.modal.focus();
          return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      handleTouchStart(event) {
        if (!this.isMobileViewport() || event.touches.length !== 1) return;
        const touch = event.touches[0];
        this.touchStartY = touch.clientY;
        this.touchStartX = touch.clientX;
        this.touchStartedAt = Date.now();
        this.dragOffset = 0;
      }

      handleTouchMove(event) {
        if (this.touchStartY === null || event.touches.length !== 1) return;

        const touch = event.touches[0];
        const deltaY = touch.clientY - this.touchStartY;
        const deltaX = Math.abs(touch.clientX - this.touchStartX);
        if (deltaY <= 0 || deltaX > deltaY) return;

        event.preventDefault();
        this.dragOffset = deltaY;
        this.modal.classList.add('cms-email-popup__modal--dragging');
        this.modal.style.transform = `translateY(${deltaY}px)`;
      }

      handleTouchEnd() {
        if (this.touchStartY === null) return;

        const elapsed = Math.max(1, Date.now() - this.touchStartedAt);
        const velocity = this.dragOffset / elapsed;
        const shouldClose = this.dragOffset >= 80 || velocity >= 0.5;

        this.touchStartY = null;
        this.touchStartX = null;
        this.touchStartedAt = null;

        if (shouldClose) {
          this.close();
        } else {
          this.resetDrag();
        }
      }

      resetDrag() {
        this.dragOffset = 0;
        this.modal?.classList.remove('cms-email-popup__modal--dragging');
        if (this.modal) this.modal.style.transform = '';
      }

      isMobileViewport() {
        return window.matchMedia('(max-width: 749px)').matches;
      }

      shouldSuppressForCourseMaterials() {
        const entrySignal = this.hasCourseMaterialsEntrySignal();
        const entryWasEvaluated = this.getSessionValue(this.entryEvaluatedKey) === '1';

        if (!entryWasEvaluated) {
          this.setSessionValue(this.entryEvaluatedKey, '1');
          if (entrySignal) this.setSessionValue(this.courseStorageKey, '1');
        }

        return (
          this.getSessionValue(this.courseStorageKey) === '1' || this.isCurrentCourseMaterialsPage()
        );
      }

      hasCourseMaterialsEntrySignal() {
        if (this.isCurrentCourseMaterialsPage()) return true;
        if (window.__follettDiscoverViewFlow === true) return true;

        const params = new URLSearchParams(window.location.search);
        const courseParamNames = new Set([
          'cmrp_flow',
          'shopby',
          'courserefid',
          'coursedisplayname',
          'sectiondisplayname',
          'departmentdisplayname',
          'studentid',
          'ssologin',
        ]);

        for (const [name] of params.entries()) {
          if (courseParamNames.has(name.toLowerCase())) return true;
        }

        const sourceText = `${window.location.search} ${document.referrer || ''}`.toLowerCase();
        if (this.parseList(this.dataset.integrationTokens).some((token) => sourceText.includes(token))) {
          return true;
        }

        return ['cmrp_flow', 'cmrp_initial_flow', 'cmrp_sso_prefill', 'cmrp_student_id_active', 'sessionToken'].some(
          (key) => Boolean(this.getSessionValue(key))
        );
      }

      isCurrentCourseMaterialsPage() {
        const pathname = window.location.pathname.toLowerCase().replace(/\/$/, '') || '/';
        const excludedPaths = this.parseList(this.dataset.excludedPaths).map((path) => {
          const normalized = path.startsWith('/') ? path : `/${path}`;
          return normalized.toLowerCase().replace(/\/$/, '');
        });

        if (
          excludedPaths.some(
            (path) => pathname === path || (path !== '/' && pathname.startsWith(`${path}/`))
          )
        ) {
          return true;
        }

        const templateName = String(this.dataset.templateName || '').toLowerCase();
        const templateSuffix = String(this.dataset.templateSuffix || '').toLowerCase();
        const excludedTemplates = this.parseList(this.dataset.excludedTemplates);

        return excludedTemplates.some(
          (template) => template === templateSuffix || template === templateName
        );
      }

      parseList(value) {
        return String(value || '')
          .split(/[\n,|]+/)
          .map((item) => item.trim().toLowerCase())
          .filter(Boolean);
      }

      getAvailableStorage(storageName) {
        try {
          const storage = window[storageName];
          if (!storage) return null;

          const probeKey = `${this.storageKeyPrefix}:storage-probe`;
          storage.setItem(probeKey, '1');
          storage.removeItem(probeKey);
          return storage;
        } catch (error) {
          return null;
        }
      }

      getSessionValue(key) {
        if (!key || !this.sessionStorage) return null;
        try {
          return this.sessionStorage.getItem(key);
        } catch (error) {
          this.sessionStorage = null;
          return null;
        }
      }

      setSessionValue(key, value) {
        if (!key || !this.sessionStorage) return false;
        try {
          this.sessionStorage.setItem(key, value);
          return true;
        } catch (error) {
          this.sessionStorage = null;
          return false;
        }
      }

      removeSessionValue(key) {
        if (!key || !this.sessionStorage) return false;
        try {
          this.sessionStorage.removeItem(key);
          return true;
        } catch (error) {
          this.sessionStorage = null;
          return false;
        }
      }

      getLocalValue(key) {
        if (!key || !this.localStorage) return null;
        try {
          return this.localStorage.getItem(key);
        } catch (error) {
          this.localStorage = null;
          return null;
        }
      }

      setLocalValue(key, value) {
        if (!key || !this.localStorage) return false;
        try {
          this.localStorage.setItem(key, value);
          return true;
        } catch (error) {
          this.localStorage = null;
          return false;
        }
      }

      removeLocalValue(key) {
        if (!key || !this.localStorage) return false;
        try {
          this.localStorage.removeItem(key);
          return true;
        } catch (error) {
          this.localStorage = null;
          return false;
        }
      }

      readLocalTimestamp(key) {
        const rawValue = this.getLocalValue(key);
        if (rawValue === null) {
          return { status: this.localStorage ? 'missing' : 'unavailable' };
        }

        if (!/^\d+$/.test(rawValue)) {
          this.removeLocalValue(key);
          return { status: 'corrupt' };
        }

        const timestamp = Number(rawValue);
        if (!Number.isSafeInteger(timestamp) || timestamp <= 0) {
          this.removeLocalValue(key);
          return { status: 'corrupt' };
        }

        return { status: 'valid', value: timestamp };
      }
    }
  );
}
